import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { RecoilRoot, useRecoilState } from 'recoil';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import { authTokenState, currentProjectState } from './state/atoms';
import { adminAPI } from './api/admin';

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { 
      main: '#FFED00',
      dark: '#E8D400',
      light: '#FFF27A',
      contrastText: '#192239',
    },
    secondary: { 
      main: '#3D474E',
      dark: '#2C2F38',
    },
    background: { 
      default: '#192239',
      paper: '#2C2F38',
    },
    text: {
      primary: '#e8ecf1',
      secondary: '#b8b8c5',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Inter", system-ui, sans-serif',
    h1: { 
      fontFamily: '"Space Grotesk", "Conthrax", Montserrat, sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.03em',
    },
    h2: { 
      fontFamily: '"Space Grotesk", "Conthrax", Montserrat, sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.03em',
    },
    h3: { 
      fontFamily: '"Space Grotesk", "Conthrax", Montserrat, sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.03em',
    },
    h4: { 
      fontFamily: '"Space Grotesk", "Conthrax", Montserrat, sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.03em',
    },
    h5: { 
      fontFamily: '"Space Grotesk", "Conthrax", Montserrat, sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.03em',
    },
    h6: { 
      fontFamily: '"Space Grotesk", "Conthrax", Montserrat, sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.03em',
    },
    button: { 
      textTransform: 'none', 
      fontWeight: 600,
      fontFamily: '"Poppins", "Inter", system-ui, sans-serif',
    },
  },
  shape: { borderRadius: 14 },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(180deg, rgba(25, 34, 57, 0.95), rgba(44, 47, 56, 0.7))',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(61, 71, 78, 0.2)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '12px 24px',
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #FFED00, #E8D400)',
          boxShadow: '0 8px 18px rgba(255, 237, 0, 0.3), 0 0 20px rgba(255, 237, 0, 0.2)',
          color: '#192239',
          fontWeight: 700,
          '&:hover': {
            background: 'linear-gradient(135deg, #FFF27A, #FFED00)',
            boxShadow: '0 15px 30px rgba(255, 237, 0, 0.5), 0 0 30px rgba(255, 237, 0, 0.4)',
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.2)',
          color: '#fff',
          '&:hover': {
            borderColor: '#FFED00',
            backgroundColor: 'rgba(255, 237, 0, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: '1px solid rgba(61, 71, 78, 0.3)',
          background: 'linear-gradient(180deg, rgba(44, 47, 56, 0.6), rgba(44, 47, 56, 0.3))',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 237, 0, 0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FFED00',
            },
          },
        },
      },
    },
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authToken, setAuthToken] = useRecoilState(authTokenState);
  const [currentProject, setCurrentProject] = useRecoilState(currentProjectState);

  useEffect(() => {
    const verifyToken = async () => {
      if (authToken) {
        try {
          const response = await adminAPI.verify();
          setCurrentProject(response.project);
        } catch (error) {
          setAuthToken(null);
          localStorage.removeItem('adminToken');
        }
      }
    };

    verifyToken();
  }, [authToken, setAuthToken, setCurrentProject]);

  if (!authToken) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRoute>
              <Projects />
            </ProtectedRoute>
          }
        />
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

function App() {
  return (
    <RecoilRoot>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppContent />
        </ThemeProvider>
      </QueryClientProvider>
    </RecoilRoot>
  );
}

export default App;
